import React from "react";
import PropTypes from "prop-types";

// Detect which skeleton to show based on URL path
const detectVariant = () => {
  if (typeof window === "undefined") return "dashboard";
  const p = window.location.pathname;
  if (
    p.includes("forgot-password") ||
    p.includes("verify") ||
    p.includes("how-it-works")
  ) {
    return "auth";
  }
  if (p.includes("dashboard")) return "dashboard";
  // Default: login/index style (auth-style)
  return "auth";
};

// Skeletons tailored to loyalty pages
// Pass auth={true|false} to explicitly pick layout. Defaults to auth.
const Loader = ({ text = "Just a moment...", auth = true, variant }) => {
  // Back-compat: if `auth` is explicitly set, use it; else use variant or detect
  const mode = typeof auth === "boolean" ? (auth ? "auth" : "dashboard") : (variant || detectVariant());

  // Shared left banner pane used in both modes
  const leftPane = (
    <div className="hidden lg:block w-[40%] relative">
      <div className="absolute inset-0 bg-gray-300 animate-pulse" />
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute left-1/2 -translate-x-1/2 top-28 text-center text-white">
        <div className="h-20 w-20 rounded-full bg-white/30 mx-auto" />
        <div className="h-6 w-48 bg-white/40 rounded mt-6 mx-auto" />
        <div className="h-4 w-64 bg-white/30 rounded mt-4 mx-auto" />
      </div>
    </div>
  );

  if (mode === "dashboard") {
    return (
      <div className="min-h-screen w-full bg-gray-50 flex">
        {/* Left hero/banner pane (shared) */}
        {leftPane}

        {/* Right content pane */}
        <div className="flex-1 flex flex-col">
          {/* Top bar */}
          <div className="w-full h-[64px] border-b border-gray-200 bg-white flex items-center justify-end px-6">
            <div className="flex items-center gap-4">
              <div className="h-7 w-7 rounded-full bg-gray-200 animate-pulse" />
              <div className="h-10 w-36 rounded-full bg-gray-100 animate-pulse" />
            </div>
          </div>

          {/* Header section in content */}
          <div className="px-6 lg:px-10 py-6 border-b">
            <div className="flex items-center gap-20">
              <div>
                <div className="h-4 w-28 bg-gray-300 rounded" />
                <div className="h-8 w-20 bg-gray-400 rounded mt-2" />
              </div>
              <div>
                <div className="h-4 w-40 bg-gray-200 rounded" />
                <div className="flex items-center gap-3 mt-3">
                  <div className="h-9 w-28 rounded-md bg-gray-300" />
                  <div className="h-9 w-36 rounded-md bg-gray-100" />
                </div>
              </div>
            </div>
          </div>

          {/* Rewards grid/empty state */}
          <div className="px-6 lg:px-10 py-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                >
                  <div className="h-48 bg-gray-200 animate-pulse" />
                  <div className="p-5">
                    <div className="h-4 w-2/3 bg-gray-200 rounded mb-3" />
                    <div className="h-4 w-1/3 bg-gray-100 rounded mb-5" />
                    <div className="flex justify-between items-center">
                      <div className="h-7 w-24 bg-gray-200 rounded" />
                      <div className="h-8 w-20 bg-gray-200 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // AUTH variant: left banner, right form
  return (
    <div className="min-h-screen w-full bg-white flex">
      {/* Left banner (shared) */}
      {leftPane}

      {/* Right auth form */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-lg">
          <div className="h-8 w-48 bg-gray-300 rounded mb-3" />
          <div className="h-4 w-64 bg-gray-200 rounded mb-10" />

          {/* Inputs */}
          <div className="space-y-5">
            <div>
              <div className="h-4 w-40 bg-gray-200 rounded mb-2" />
              <div className="h-12 w-full bg-gray-100 rounded-md" />
            </div>
            <div>
              <div className="h-4 w-28 bg-gray-200 rounded mb-2" />
              <div className="h-12 w-full bg-gray-100 rounded-md" />
              <div className="h-4 w-28 bg-gray-100 rounded mt-2 ml-auto" />
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-8 space-y-4">
            <div className="h-12 w-full bg-gray-300 rounded-md" />
            <div className="h-12 w-full bg-gray-100 rounded-md" />
          </div>
        </div>
      </div>
    </div>
  );
};

Loader.propTypes = {
  text: PropTypes.string,
  auth: PropTypes.bool,
  variant: PropTypes.oneOf(["dashboard", "auth"]),
};

export { Loader };
